import React from 'react';
import { useQuery } from '@apollo/client';
import { useParams } from 'react-router-dom';
import { FeaturedJobs as FeaturedJobsType, FeatureTopic } from '~/generated';
import { listFeaturedJobs } from '~/queries/featuredJob';
import { showFeatureTopic } from '~/queries/featureTopic';
import {
  Box,
  Heading,
  Text,
  Flex,
  VStack,
  Image,
  Button,
  Link,
  useBreakpointValue,
  Badge,
  Wrap,
} from '@chakra-ui/react';
import { LikeButton } from '../molecules';

const FeaturedJobs = (): JSX.Element => {
  const { slug } = useParams<{ slug: string }>();

  const { data: featureTopicData } = useQuery<{
    featureTopicBySlug: FeatureTopic;
  }>(showFeatureTopic, {
    variables: { slug },
  });

  const { data: featuredJobsData } = useQuery<{
    featuredJobs: FeaturedJobsType;
  }>(listFeaturedJobs, {
    variables: {
      filters: [{ field: 'slug', value: slug }],
      sort: { field: 'createdAt', order: 'DESC' },
    },
  });

  const headingSize = useBreakpointValue({ base: 'sm', md: 'md' });

  if (!(slug && featuredJobsData && featureTopicData)) {
    return <div />;
  }

  const topic = featureTopicData.featureTopicBySlug;
  const jobs = featuredJobsData.featuredJobs.data.map(
    (featuredJob) => featuredJob.job,
  );

  return (
    <Box>
      <VStack p={{ base: 4, md: 10 }} spacing={5}>
        <Heading textAlign="center" wordBreak="break-word" size="lg">
          {topic.title}
        </Heading>
        <Text textAlign="center" whiteSpace="pre-wrap">
          興味ありを押すことで企業からメッセージが届く可能性があります。
        </Text>
        {jobs.map((job) => (
          <Box
            key={job.id}
            boxShadow="xl"
            rounded="base"
            bg="white"
            maxW="600px"
            mb={4}
            border="1px solid #E8E8E8"
          >
            {job.coverImage && (
              <Image
                src={job.coverImage}
                fit="cover"
                objectPosition="center"
                w="100%"
                maxW={640}
                maxH={360}
              />
            )}
            <Box py={2} px={5}>
              <Wrap my={2}>
                {job.characteristicLabels.map((characteristicLabel) => (
                  <Badge
                    key={characteristicLabel.id}
                    sx={{ p: 1, fontSize: 'xs' }}
                  >
                    {characteristicLabel.name}
                  </Badge>
                ))}
              </Wrap>
              <Text color="gray.500">{job.company.name}</Text>
              <Heading my={3} wordBreak="break-word" size={headingSize}>
                {job.title}
              </Heading>
              <Text
                overflow="hidden"
                display="-webkit-box"
                sx={{
                  wordBreak: 'break-word',
                  overflow: 'hidden',
                  display: '-webkit-box',
                  WebkitLineClamp: '3',
                  WebkitBoxOrient: 'vertical',
                  textOverflow: 'ellipsis',
                }}
              >
                {job.descriptionText}
              </Text>
              <Flex
                justify="space-around"
                flexDirection="row"
                m={{ base: '10px', md: '20px 10px 10px 10px' }}
                alignItems="center"
              >
                <LikeButton job={job} />
                <Link href={`/jobs/${job.id}`} display="flex">
                  <Button
                    minW={{ base: '100px', md: '200px' }}
                    size="md"
                    flexGrow={1}
                    my={{ base: 2, md: 0 }}
                  >
                    募集詳細
                  </Button>
                </Link>
              </Flex>
            </Box>
          </Box>
        ))}
      </VStack>
    </Box>
  );
};

export default FeaturedJobs;
